/**
 * Validates input text to ensure it's safe for `latin1_swedish_ci` collation.
 */

import { LATIN1_MAX_CODEPOINT, REPLACEMENT_CHARACTER, UNICODE_INVISIBLE_RE, UNICODE_WHITESPACE_RE } from './constants';
import type { MaybeString } from './latin1SafeTextField.types';
import { getCodePoint, isControlCharacter } from './utils';

export type FieldValidationResult = {
  hasError: boolean;
  errorMessage?: string;
};

export const HIDDEN_FORMATTING_CHAR_ERROR = 'Some characters may be invisible (often introduced by copy-paste).';

export const isHiddenChar = (ch: string) => {
  const codePoint = getCodePoint(ch);
  return (
    codePoint !== undefined &&
    (UNICODE_INVISIBLE_RE.test(ch) || UNICODE_WHITESPACE_RE.test(ch) || isControlCharacter(codePoint))
  );
};

export const describeUnsupportedCharacter = (ch: string): string => {
  const codePoint = getCodePoint(ch);
  if (codePoint === undefined) {
    return "'?'";
  }
  return isHiddenChar(ch) ? `'an unsupported formatting character'` : `'${ch}'`;
};

export const getInvalidLatin1Chars = (input?: MaybeString): string[] => {
  const invalidChars = [...(input ?? '')].filter((ch) => {
    const codePoint = ch.codePointAt(0);
    return codePoint !== undefined && (codePoint > LATIN1_MAX_CODEPOINT || ch === REPLACEMENT_CHARACTER);
  });

  return [...new Set(invalidChars)];
};

export const buildLatin1UnsupportedCharsMessage = (invalidChars: readonly string[]): string | undefined => {
  const uniqueInvalidChars = [...new Set(invalidChars)];

  if (uniqueInvalidChars.length === 0) {
    return undefined;
  }

  const formattedUnsupportedChars = uniqueInvalidChars.map(describeUnsupportedCharacter).join(', ');
  const isPlural = uniqueInvalidChars.length > 1;

  return (
    `The ${isPlural ? 'characters' : 'character'} ${formattedUnsupportedChars} ${isPlural ? 'are' : 'is'} not supported. ` +
    `Please remove or replace ${isPlural ? 'them' : 'it'} to continue.` +
    `${uniqueInvalidChars.some(isHiddenChar) ? ` ${HIDDEN_FORMATTING_CHAR_ERROR}` : ''}`
  );
};

/**
 * Validates single input value for Latin 1 safety.
 */

export const validateLatin1SafeInput = (input?: MaybeString): FieldValidationResult => {
  const message = buildLatin1UnsupportedCharsMessage(getInvalidLatin1Chars(input));

  if (!message) {
    return { hasError: false };
  }

  return {
    hasError: true,
    errorMessage: message,
  };
};

/**
 * Validates multiple input values for Latin 1 safety.
 */

export type FieldsValidationResult = {
  valid: boolean;
  errors: Record<string, string>;
};

export const validateLatin1Fields = (fields: Record<string, MaybeString>): FieldsValidationResult => {
  const errors: Record<string, string> = {};

  for (const key in fields) {
    const { hasError, errorMessage } = validateLatin1SafeInput(fields[key]);
    if (hasError && errorMessage) {
      errors[key] = errorMessage;
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
};
